// src/screens/NotificationsScreen.js
// ============================================================================
// NOTIFICATIONS SCREEN - Full list of all notifications
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView,
  ActivityIndicator, RefreshControl, Image
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { getAuth } from 'firebase/auth';
import { collection, query, orderBy, limit, getDocs, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Footer from '../components/Footer';

// Gold d20 asset — the same die the Header balance and Wallet use. Crit-granting
// notification types show it instead of the dice emoji.
const CRIT_DIE = require('../../assets/20_gdice.webp');
const CRIT_DICE_TYPES = new Set(['crit_earned', 'crit_purchase_success', 'host_reward_earned']);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function NotificationsScreen({ navigation }) {
  const isFocused = useIsFocused();
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;

  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================
  const fetchNotifications = useCallback(async () => {
    if (!currentUserId) return;

    try {
      const notifsQuery = query(
        collection(db, 'users', currentUserId, 'notifications'),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
      const snapshot = await getDocs(notifsQuery);
      const notifs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setNotifications(notifs);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (isFocused) {
      fetchNotifications();
    }
  }, [isFocused, fetchNotifications]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchNotifications();
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleNotificationPress = async (notification) => {
    // Mark as read
    if (!notification.read) {
      try {
        await updateDoc(doc(db, 'users', currentUserId, 'notifications', notification.id), { read: true });
        setNotifications(prev => prev.map(n => 
          n.id === notification.id ? { ...n, read: true } : n
        ));
      } catch (e) {
        console.error('Failed to mark as read:', e);
      }
    }

    // Navigate based on type (match the strings the backend actually emits).
    const type = notification.type;
    const go = (route, params) => { try { navigation.navigate(route, params); } catch (e) {} };
    if (type === 'stream_live') {
      if (notification.streamerUid) go('StreamWatch', { channel: notification.streamerUid });
    } else if (type === 'stream_new_subscriber') {
      go('Streams');
    } else if (type === 'stream_rumble') {
      // A squad Rumble opens that squad stream; a solo stream Rumble opens the
      // creator's own metrics. (Notification data is stored at the top level.)
      if (notification.targetType === 'team' && notification.targetId) go('TeamStream', { teamStreamId: notification.targetId });
      else go('CreatorMetrics');
    } else if (type === 'match_ready' || type === 'lobby_ready' || type === 'lobby_regenerated' || type === 'round_advance') {
      go('MatchLobby', { matchId: notification.matchId, eventId: notification.eventId });
    } else if (
      type === 'match_result_win' || type === 'match_result_loss' ||
      type === 'tournament_won' || type === 'distribution_received' ||
      type === 'host_reward_earned' || type === 'crit_earned' ||
      type === 'rumble_received' ||
      type === 'event_rumble_share' || type === 'rumble_refunded' ||
      type === 'event_starting' || type === 'event_canceled' ||
      type === 'run_it_back_invite' ||
      type === 'match_result' // legacy/demo alias
    ) {
      // Registered route is 'EventDetailScreen' ('EventDetail' is not a route).
      if (notification.eventId) go('EventDetailScreen', { eventId: notification.eventId });
    } else if (type === 'sellout_certified' || type === 'full_crown_earned') {
      // Certification lands on whichever surface it belongs to (event or league).
      if (notification.eventId) go('EventDetailScreen', { eventId: notification.eventId });
      else if (notification.leagueId) go('LeagueDetail', { leagueId: notification.leagueId });
    } else if (type === 'league_run_it_back_invite') {
      if (notification.leagueId) go('LeagueDetail', { leagueId: notification.leagueId });
    } else if (type === 'membership_team_new_member' || type === 'membership_team_status') {
      // Captain notices carry the team at the top level (targetType 'team',
      // targetId). Without one, the screen falls back to the viewer's own team.
      go('CreatorTeamMemberships', notification.targetId ? { teamId: notification.targetId } : undefined);
    } else if (type === 'stream_new_member' || type === 'membership_creator_status') {
      go('CreatorMemberships');
    } else if (
      type === 'membership_active' || type === 'membership_payment_failed' ||
      type === 'membership_notice'
    ) {
      go('Memberships');
    } else if (
      type === 'subscription_active' || type === 'crit_purchase_success' ||
      type === 'plan_renewal_reminder' || type === 'subscription_payment_failed'
    ) {
      go('Subscription');
    } else if (type === 'season_started') {
      go('Leaderboard');
    } else if (type === 'referral_pending') {
      go('Profile');
    } else if (type === 'payout') {
      // Legacy alias (no live emitter) — cash earnings surface.
      go('Wallet');
    }
  };

  const handleMarkAllRead = async () => {
    const unread = notifications.filter(n => !n.read);
    if (unread.length === 0) return;

    try {
      const batch = writeBatch(db);
      unread.forEach(n => {
        batch.update(doc(db, 'users', currentUserId, 'notifications', n.id), { read: true });
      });
      await batch.commit();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================
  // Crit-granting types render the gold d20 asset (same die as Header/Wallet)
  // instead of the dice emoji — see CRIT_DIE below.
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'match_ready': return '🎮';
      case 'lobby_ready': return '🏟️';
      case 'lobby_regenerated': return '🔄';
      case 'stream_live': return '📺';
      case 'stream_new_subscriber': return '💛';
      // Channel memberships (creator side, then the member's own notices)
      case 'stream_new_member': return '⭐';
      case 'membership_creator_status': return '⚠️';
      // Team memberships (captain side; the member's own notices use the types below)
      case 'membership_team_new_member': return '👥';
      case 'membership_team_status': return '⚠️';
      case 'membership_active': return '⭐';
      case 'membership_payment_failed': return '💳';
      case 'membership_notice': return 'ℹ️';
      // Paid Plan billing notices
      case 'plan_renewal_reminder': return '📅';
      case 'subscription_payment_failed': return '💳';
      // Live backend types
      case 'match_result_win': return '🏆';
      case 'match_result_loss': return '🏁';
      case 'tournament_won': return '👑';
      case 'round_advance': return '⏫';
      case 'distribution_received': return '🎖️';
      case 'host_reward_earned': return '🎲';
      case 'crit_earned': return '🎲';
      case 'crit_purchase_success': return '🎲';
      case 'subscription_active': return '⭐';
      case 'rumble_received': return '⚡';
      case 'stream_rumble': return '⚡';
      case 'event_rumble_share': return '⚡';
      case 'rumble_refunded': return '↩️';
      case 'season_started': return '🏆';
      case 'referral_pending': return '🎁';
      case 'event_starting': return '⏰';
      case 'event_canceled': return '❌';
      case 'match_under_review': return '🛡️';
      case 'dispute_filed': return '⚠️';
      case 'connection_advisory': return '📶';
      case 'sellout_certified': return '👑';
      case 'full_crown_earned': return '👑';
      case 'run_it_back_invite': return '🔁';
      case 'league_run_it_back_invite': return '🔁';
      // Legacy/demo aliases
      case 'match_result': return '🏆';
      case 'payout': return '💰';
      default: return '📢';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} days ago`;
    return date.toLocaleDateString();
  };

  const groupNotificationsByDate = () => {
    const groups = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    notifications.forEach(notif => {
      const date = notif.createdAt?.toDate ? notif.createdAt.toDate() : new Date(notif.createdAt);
      date.setHours(0, 0, 0, 0);

      let key;
      if (date.getTime() === today.getTime()) {
        key = 'Today';
      } else if (date.getTime() === yesterday.getTime()) {
        key = 'Yesterday';
      } else {
        key = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(notif);
    });

    return groups;
  };

  // ============================================================================
  // LOADING STATE
  // ============================================================================
  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#FCD000" />
        <Text style={styles.loadingText}>Loading notifications...</Text>
      </View>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  const groupedNotifications = groupNotificationsByDate();
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor="#FCD000"
          />
        }
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <TouchableOpacity onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))} style={styles.backButton}>
              <Text style={styles.backButtonText}>←</Text>
            </TouchableOpacity>
            <Text style={styles.title}>Notifications</Text>
          </View>
          {unreadCount > 0 && (
            <TouchableOpacity onPress={handleMarkAllRead} style={styles.markAllButton}>
              <Text style={styles.markAllText}>Mark all read</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Empty State */}
        {notifications.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyIcon}>🔔</Text>
            <Text style={styles.emptyTitle}>No notifications yet</Text>
            <Text style={styles.emptySubtitle}>
              You'll see go-live alerts, match alerts, lobby info, and updates here
            </Text>
          </View>
        ) : (
          /* Grouped Notifications */
          Object.entries(groupedNotifications).map(([dateLabel, notifs]) => (
            <View key={dateLabel} style={styles.dateGroup}>
              <Text style={styles.dateLabel}>{dateLabel}</Text>
              {notifs.map((notif) => (
                <TouchableOpacity
                  key={notif.id}
                  style={[
                    styles.notificationCard,
                    !notif.read && styles.notificationCardUnread
                  ]}
                  onPress={() => handleNotificationPress(notif)}
                  activeOpacity={0.7}
                >
                  {CRIT_DICE_TYPES.has(notif.type) ? (
                    <Image source={CRIT_DIE} style={styles.notificationDieIcon} resizeMode="contain" />
                  ) : (
                    <Text style={styles.notificationIcon}>{getNotificationIcon(notif.type)}</Text>
                  )}
                  <View style={styles.notificationContent}>
                    <Text style={[
                      styles.notificationTitle,
                      !notif.read && styles.notificationTitleUnread
                    ]}>
                      {notif.title}
                    </Text>
                    <Text style={styles.notificationBody} numberOfLines={2}>
                      {notif.body}
                    </Text>
                    <Text style={styles.notificationTime}>{formatTime(notif.createdAt)}</Text>
                  </View>
                  {!notif.read && <View style={styles.unreadIndicator} />}
                </TouchableOpacity>
              ))}
            </View>
          ))
        )}

        <View style={{ height: 40 }} />
      </ScrollView>

      <Footer />
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0D0D12',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },

  // Loading
  loadingContainer: {
    flex: 1,
    backgroundColor: '#0D0D12',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#888',
    marginTop: 12,
    fontSize: 14,
  },

  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    marginRight: 12,
    padding: 4,
  },
  backButtonText: {
    color: '#FCD000',
    fontSize: 24,
  },
  title: {
    fontSize: 28,
    fontWeight: '800',
    color: '#FFF',
  },
  markAllButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  markAllText: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: '600',
  },

  // Empty State
  emptyState: {
    alignItems: 'center',
    paddingVertical: 80,
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 16,
    opacity: 0.3,
  },
  emptyTitle: {
    color: '#888',
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 8,
  },
  emptySubtitle: {
    color: '#555',
    fontSize: 14,
    textAlign: 'center',
  },

  // Date Groups
  dateGroup: {
    marginBottom: 24,
  },
  dateLabel: {
    color: '#888',
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
  },

  // Notification Card
  notificationCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#1A1A24',
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#2A2A36',
  },
  notificationCardUnread: {
    backgroundColor: '#1E1E2A',
    borderColor: '#FCD00030',
  },
  notificationIcon: {
    fontSize: 28,
    marginRight: 14,
    marginTop: 2,
  },
  notificationDieIcon: {
    width: 28,
    height: 28,
    marginRight: 14,
    marginTop: 2,
  },
  notificationContent: {
    flex: 1,
  },
  notificationTitle: {
    color: '#CCC',
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 4,
  },
  notificationTitleUnread: {
    color: '#FFF',
  },
  notificationBody: {
    color: '#888',
    fontSize: 14,
    lineHeight: 20,
  },
  notificationTime: {
    color: '#555',
    fontSize: 12,
    marginTop: 8,
  },
  unreadIndicator: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#FCD000',
    marginLeft: 8,
    marginTop: 6,
  },
});